import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Check, Clock, ArrowRight, MapPin, HelpCircle } from "lucide-react";
import useLenis from "../hooks/useLenis";
import useEngagementTracking from "../hooks/useEngagement";
import useSEO, { localBusinessLd, faqLd, breadcrumbLd } from "../hooks/useSEO";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import FloatingWhatsApp from "../components/site/FloatingWhatsApp";
import Gallery from "../components/site/Gallery";
import Reviews from "../components/site/Reviews";
import InterventoRapido from "../components/site/InterventoRapido";
import QuoteWizard from "../components/site/QuoteWizard";
import { WhatsAppButton, PreventivoButton, EmailButton } from "../components/site/CTAs";
import { Reveal, Overline } from "../components/site/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { BENEFITS } from "../data";
import { SEO_BY_SLUG, getRelated } from "../data/seoPages";
import { neutralizeWA } from "../config";

export default function SEOLanding() {
  const { slug } = useParams();
  const page = SEO_BY_SLUG[slug];
  useLenis();
  useEngagementTracking();
  useSEO({
    title: page?.title,
    description: neutralizeWA(page?.description),
    keywords: `${page?.keyword}, muratore roma, imbianchino roma, piccoli lavori edili roma`,
    path: `/${slug}`,
    jsonLd: page
      ? [
          localBusinessLd,
          faqLd(page.faqs.map((f) => ({ q: neutralizeWA(f.q), a: neutralizeWA(f.a) }))),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: page.category === "zone" ? "Zone" : "Servizi", path: "/" },
            { name: page.h1, path: `/${slug}` },
          ]),
        ]
      : [],
  });

  if (!page) return <Navigate to="/" replace />;

  const related = getRelated(page);

  return (
    <div className="bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white pt-24 sm:pt-28 lg:pt-32 pb-16">
        <div className="pointer-events-none absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-brand-orange/10 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-ink/50 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-navy">Home</Link>
            <ChevronRight size={14} />
            <span className="text-navy font-medium">{page.h1}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy/70">
              <MapPin size={14} className="text-brand-orange" /> {page.category === "zone" ? "Zona di Roma" : "Servizio a Roma"}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 font-display font-extrabold tracking-tighter text-navy text-4xl sm:text-6xl leading-[1.0]"
          >
            {page.h1}
          </motion.h1>

          {page.intro.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
              className="mt-5 text-ink/70 text-base sm:text-lg leading-relaxed"
            >
              {neutralizeWA(p)}
            </motion.p>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3"
          >
            <WhatsAppButton size="lg" testid="seo-hero-whatsapp" label="Preventivo su WhatsApp" location={`seo-${slug}`} />
            <PreventivoButton size="lg" variant="outlineDark" testid="seo-hero-preventivo" label="Preventivo Online" location={`seo-${slug}`} />
          </motion.div>
        </div>
      </section>

      {/* Service description */}
      <section className="bg-surface py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal><Overline>Il servizio</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl">{page.serviceTitle}</h2>
          </Reveal>
          {page.serviceBody.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.05}>
              <p className="mt-5 text-ink/70 text-base sm:text-lg leading-relaxed">{neutralizeWA(p)}</p>
            </Reveal>
          ))}

          {/* List (zones / service items) */}
          <Reveal delay={0.1} className="mt-10">
            <h3 className="font-display font-bold text-xl text-navy mb-4">{page.listTitle}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {page.list.map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <Check size={16} className="text-brand-orange shrink-0" />
                  <span className="text-sm font-medium text-ink">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 flex flex-col sm:flex-row gap-3">
            <WhatsAppButton testid="seo-service-whatsapp" label="Chiedi su WhatsApp" location={`seo-${slug}`} />
            <PreventivoButton variant="outlineDark" testid="seo-service-preventivo" label="Richiedi Preventivo" location={`seo-${slug}`} />
          </Reveal>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <Reveal><Overline>Perché sceglierci</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl">
              Perché scegliere Muratore Imbianchino Roma
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {BENEFITS.slice(0, 8).map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={(i % 4) * 0.06}>
                  <Icon size={26} strokeWidth={1.6} className="text-brand-orange" />
                  <h3 className="mt-3 font-display font-bold text-lg text-navy">{b.title}</h3>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed">{b.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process + timing */}
      <section className="bg-navy text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
          <Overline>Come lavoriamo</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl">La nostra procedura</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {page.process.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 h-full">
                  <span className="font-display font-extrabold text-3xl text-brand-orange">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display font-bold text-lg">{neutralizeWA(s.title)}</h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">{neutralizeWA(s.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-8 flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 p-5">
            <Clock size={22} className="text-brand-orange shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display font-bold">Tempistiche indicative</h3>
              <p className="mt-1 text-sm text-white/70 leading-relaxed">{neutralizeWA(page.timing)}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <Gallery />

      <InterventoRapido />

      <Reviews />

      <QuoteWizard />

      {/* FAQ */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal><Overline>Domande frequenti</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl flex items-center gap-3">
              <HelpCircle className="text-brand-orange" size={30} /> FAQ
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              {page.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`seo-faq-${i}`}>
                  <AccordionTrigger className="text-base font-semibold text-navy py-5 hover:no-underline">
                    {neutralizeWA(f.q)}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] text-ink/70 leading-relaxed">{neutralizeWA(f.a)}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Related internal links */}
      {related.length > 0 && (
        <section className="bg-surface py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <Overline>Pagine correlate</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-2xl sm:text-3xl">Potrebbe interessarti anche</h2>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/${r.slug}`}
                  data-testid={`seo-related-${r.slug}`}
                  className="group rounded-2xl border border-gray-200 bg-white p-6 hover:border-navy/30 hover:shadow-lg transition-[box-shadow,border-color] duration-300"
                >
                  <h3 className="font-display font-bold text-navy text-lg">{r.h1}</h3>
                  <p className="mt-2 text-sm text-ink/55 line-clamp-2">{neutralizeWA(r.description)}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange">
                    Scopri di più <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="relative bg-navy-deep text-white py-16 sm:py-24 overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 text-center">
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">{page.h1}: richiedi ora il preventivo</h2>
          <p className="mt-4 text-white/75 text-lg">{neutralizeWA("Scrivici su WhatsApp o compila il modulo. Preventivo gratuito e risposta rapida.")}</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <WhatsAppButton size="lg" testid="seo-final-whatsapp" label="Preventivo su WhatsApp" location={`seo-${slug}`} />
            <PreventivoButton size="lg" variant="orange" testid="seo-final-preventivo" label="Preventivo Online" location={`seo-${slug}`} />
            <EmailButton size="lg" variant="outlineLight" testid="seo-final-email" label="Via Email" location={`seo-${slug}`} />
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
